import React, { useEffect, useState } from 'react';
import { Dropdown } from '@dexma/ui-components';
import gtm from 'adapter/in/reports/config/gtm';
import i18n from 'asset/translation';
import useTextBlock from 'adapter/in/common/widget/TextBlock/useTextBlock';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { CreateTextBlockCriteriaBuilder } from 'use-case/widgets/create/createTextBlockCriteria';
import { useUseCaseAlerts } from 'adapter/in/common/hooks/useUseCaseAlerts';
import { dispatchReportLayoutsData } from 'adapter/in/reports/contexts/ReportLayoutDataContext';
import { dispatchTextBlockData } from 'adapter/in/reports/contexts/TextBlockContext';
import { isSome } from 'fp-ts/lib/Option';
import { ReadLayoutsCriteriaBuilder } from 'use-case/layout/read/readLayoutsCriteria';
import styled, { css } from 'styled-components';
import { ReportAnalysisCoordinates } from 'adapter/in/common/analysis/reportAnalysisCoordinates';
import { useNewAnalysisFromTemplateOverlayModal } from 'adapter/in/reports/components/AddWidgetDropdown/useNewAnalysisFromTemplateOverlayModal';
import { SaveReportTemplateLayoutUseCase } from 'adapter/in/reports/hooks/useSaveReportTemplateLayout';

const ADD_WIDGET_MENU_ITEM = {
    ANALYSIS: 0,
    RICH_TEXT: 1,
} as const;

const ADD_WIDGET_DROPDOWN_CONTENT = [
    {
        id: ADD_WIDGET_MENU_ITEM.ANALYSIS,
        text: i18n.t('advancedAnalytics.analysis.title'),
        dataId: gtm.addWidget,
    },
    {
        id: ADD_WIDGET_MENU_ITEM.RICH_TEXT,
        text: i18n.t('widgets.widgetMenu.text'),
        dataId: gtm.addTextWidget,
    },
];

type AddWidgetDropdownProps = {
    reportTemplateId: string;
    isLayoutOverlay?: boolean;
    contextPeriod?: string;
    onTextWidgetSelected?: () => void;
    analysisCoordinates?: ReportAnalysisCoordinates;
    updateTemplateLayout?: SaveReportTemplateLayoutUseCase;
};

export const AddWidgetDropdown = ({
    reportTemplateId,
    isLayoutOverlay,
    contextPeriod,
    onTextWidgetSelected,
    analysisCoordinates,
    updateTemplateLayout,
}: AddWidgetDropdownProps) => {
    const { createTextBlock } = useTextBlock();
    const { execute: create, isLoading, data, error: createError } = useCase(createTextBlock, false);
    const { getLayouts } = dispatchReportLayoutsData();
    const [isCreating, setIsCreating] = useState(false);
    const { setEditorBeingEdited } = dispatchTextBlockData();
    const { handleOnConfirmModal, onConfirmModal } = useNewAnalysisFromTemplateOverlayModal(
        reportTemplateId,
        !!isLayoutOverlay,
        contextPeriod,
        analysisCoordinates,
        updateTemplateLayout
    );

    useUseCaseAlerts(data, createError, {
        successMessage: 'reports.reportTemplate.textBlockCreateSuccess',
        errorMessage: 'reports.reportTemplate.textBlockCreateError',
    });

    useEffect(() => {
        if (!isLoading && isCreating && reportTemplateId) {
            getLayouts(ReadLayoutsCriteriaBuilder.create().withDashboardId(reportTemplateId).build());
        }
    }, [isLoading, isCreating, reportTemplateId]);

    useEffect(() => {
        if (isSome(data)) {
            setEditorBeingEdited(data.value.id);
            setIsCreating(false);
        }
    }, [data]);

    const handleItemSelected = (key: number) => {
        const selectedItem = ADD_WIDGET_DROPDOWN_CONTENT[key];
        if (!selectedItem) return;

        switch (selectedItem.id) {
            case ADD_WIDGET_MENU_ITEM.ANALYSIS:
                handleOnConfirmModal();
                break;
            case ADD_WIDGET_MENU_ITEM.RICH_TEXT:
                if (isLayoutOverlay) {
                    onTextWidgetSelected?.();
                    break;
                }
                create(CreateTextBlockCriteriaBuilder.create().withDashboardId(reportTemplateId).withRichText('').build());
                setIsCreating(true);
                break;
            default:
                break;
        }
    };
    return (
        <StyledAddWidgetDropdown data-testid={isLayoutOverlay ? 'add-widget-overlay' : 'add-widget'} $isLayoutOverlay={isLayoutOverlay}>
            <Dropdown
                content={ADD_WIDGET_DROPDOWN_CONTENT}
                text={i18n.t('dashboards.edit.addWidget')}
                trigger={['click']}
                variant='primary'
                onItemSelected={handleItemSelected}
                dataId={gtm.addWidget}
            />
            {onConfirmModal}
        </StyledAddWidgetDropdown>
    );
};

const StyledAddWidgetDropdown = styled.div<{ $isLayoutOverlay?: boolean }>`
    display: inline-flex;

    ${({ $isLayoutOverlay }) =>
        $isLayoutOverlay &&
        css`
            .dropdown-button {
                height: 1.5rem;
                padding: 0 0.75rem;
                font-size: 0.75rem;
                line-height: 1.5rem;
                white-space: nowrap;
            }
        `}
`;
